import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CreditCard, Check, Loader2, ArrowLeft } from 'lucide-react';
import { useGame } from '../context/GameContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentPage = () => {
  const navigate = useNavigate();
  const { playerName } = useGame();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Helper function to create profile from pending data
  const createPendingProfile = async () => {
    const pendingProfile = localStorage.getItem('pendingProfile');
    if (pendingProfile) {
      try {
        const profileData = JSON.parse(pendingProfile);
        await axios.post(`${API}/kids/create-profile`, profileData);
        localStorage.removeItem('pendingProfile'); // Clear after successful creation
        console.log('Profile created successfully:', profileData.player_name);
      } catch (error) {
        console.error('Error creating profile:', error);
        // Don't block navigation if profile creation fails
      }
    }
  };

  useEffect(() => {
    if (!playerName) {
      navigate('/');
      return;
    }

    // Check if returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setCheckingStatus(true);
      pollPaymentStatus(sessionId);
    }
  }, [playerName, navigate]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please contact support.');
      setCheckingStatus(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        // Payment successful - create profile now
        await createPendingProfile();
        // Navigate to age selection
        navigate('/age-selection');
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setCheckingStatus(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Error verifying payment. Please try again.');
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      console.log('Creating subscription for:', playerName);
      console.log('Origin URL:', originUrl);
      
      const response = await axios.post(`${API}/payments/create-subscription`, {
        origin_url: originUrl,
        player_name: playerName
      });

      console.log('Payment response:', response.data);

      if (response.data.url) {
        console.log('Redirecting to Stripe:', response.data.url);
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const handleTryDemo = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Create the profile first (if pending)
      await createPendingProfile();
      
      // Get parent email from localStorage (from ChildSetup flow)
      const pendingParentEmail = localStorage.getItem('pendingParentEmail');
      
      // Create a demo account with active subscription (no payment)
      const response = await axios.post(`${API}/player/demo-activate`, {
        player_name: playerName,
        parent_email: pendingParentEmail || null
      });
      
      if (response.data.success) {
        // Clear parent email from localStorage
        localStorage.removeItem('pendingParentEmail');
        // Navigate to age selection to start playing
        navigate('/age-selection');
      } else {
        throw new Error('Failed to activate demo');
      }
    } catch (error) {
      console.error('Demo activation error:', error);
      setError('Failed to activate demo. Please try again.');
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
      }}>
        <Card className="p-12 bg-white/95 backdrop-blur-sm shadow-2xl text-center" style={{ borderRadius: '32px' }}>
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-red-500 animate-spin" />
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Verifying Payment...</h2>
          <p className="text-gray-600">Please wait while we confirm your subscription.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
    }}>
      <div className="max-w-2xl w-full">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-lg font-semibold text-white hover:scale-110 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm shadow-2xl bounce-in" style={{ borderRadius: '32px' }}>
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-400 to-orange-400 rounded-full flex items-center justify-center">
              <CreditCard className="w-14 h-14 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Join Little Genius Hub!</h1>
            <p className="text-xl text-gray-600">Get unlimited access to all games and subjects</p>
          </div>

          <Card className="p-8 bg-gradient-to-br from-red-50 to-orange-50 border-4 border-red-200 mb-8" style={{ borderRadius: '24px' }}>
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-red-600 mb-2">$9.99</div>
              <div className="text-xl text-gray-700 font-semibold">per month</div>
            </div>

            <div className="space-y-4">
              {[
                'Unlimited access to all games',
                'All age groups (3-12 years)',
                'Math, Language, Science & Mixed subjects',
                'Quiz, Puzzle, Story & Memory games',
                'Progress tracking & achievements',
                'Cancel anytime'
              ].map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-700 text-lg">{feature}</span>
                </div>
              ))}
            </div>

            {/* Legal Disclaimer */}
            <div className="mt-6 pt-6 border-t border-red-200">
              <p className="text-sm text-gray-600 text-center leading-relaxed">
                By purchasing this subscription, you agree to our{' '}
                <Link to="/terms-of-service" className="text-red-600 hover:text-red-700 underline font-medium">
                  Terms of Service
                </Link>
                ,{' '}
                <Link to="/childrens-privacy" className="text-red-600 hover:text-red-700 underline font-medium">
                  Children's Use & Parental Consent
                </Link>
                ,{' '}
                <Link to="/privacy-policy" className="text-red-600 hover:text-red-700 underline font-medium">
                  Privacy Policy
                </Link>
                , and{' '}
                <Link to="/affiliate-terms" className="text-red-600 hover:text-red-700 underline font-medium">
                  Affiliate & Partner Terms
                </Link>
                .
              </p>
            </div>
          </Card>

          {error && (
            <Card className="p-4 mb-6 bg-red-50 border-2 border-red-300" style={{ borderRadius: '16px' }}>
              <p className="text-red-700 text-center">{error}</p>
            </Card>
          )}

          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
              borderRadius: '16px'
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-6 h-6 mr-2" />
                Subscribe Now
              </>
            )}
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">Or</span>
            </div>
          </div>

          <Button
            onClick={handleTryDemo}
            disabled={loading}
            variant="outline"
            className="w-full py-6 text-xl font-bold border-4 border-blue-400 text-blue-600 hover:bg-blue-50 transition-all duration-300 hover:scale-105"
            style={{
              borderRadius: '16px'
            }}
          >
            Try Demo (Free - No Payment)
          </Button>

          <p className="text-center text-sm text-gray-500 mt-6">
            Secure payment powered by Stripe
          </p>
        </Card>
      </div>
    </div>
  );
};

export default PaymentPage;
